import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { LayoutGrid, CalendarCheck, Settings, UserPlus, BarChart3, Table2 } from 'lucide-react';
import { toast } from 'sonner';
import PipelineView from './PipelineView';
import BoardView from './BoardView';
import AziView from './AziView';
import ClientFisa from './ClientFisa';
import SetariView from './SetariView';
import ReportsView from './ReportsView';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CrmModule({ userRole }) {
  const [view, setView] = useState('board');
  const [selectedClient, setSelectedClient] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const isBroker = userRole === 'broker';
  const isAdmin = userRole === 'admin';

  const api = useMemo(() => {
    const token = localStorage.getItem('token');
    const base = `${API_URL}/api/crm`;
    const headers = { Authorization: `Bearer ${token}` };
    const cfg = { headers, timeout: 20000 };
    return {
      get: async (p) => (await axios.get(`${base}${p}`, cfg)).data,
      post: async (p, body) => (await axios.post(`${base}${p}`, body || {}, cfg)).data,
      put: async (p, body) => (await axios.put(`${base}${p}`, body || {}, cfg)).data,
      del: async (p) => (await axios.delete(`${base}${p}`, cfg)).data,
    };
  }, []);

  const loadSettings = useCallback(async () => {
    for (let i = 0; i < 4; i++) {
      try {
        const s = await api.get('/settings');
        if (s && (s.surse || s.banci)) { setSettings(s); return; }
      } catch (e) { /* retry */ }
      await new Promise((r) => setTimeout(r, 800));
    }
  }, [api]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const openClient = (id) => { setSelectedClient(id); setView('client'); };

  const NavBtn = ({ id, icon: Icon, label }) => (
    <Button
      variant={view === id ? 'default' : 'outline'}
      size="sm"
      onClick={() => { setView(id); setSelectedClient(null); }}
      data-testid={`crm-nav-${id}`}
      className="flex items-center gap-2"
    >
      <Icon className="h-4 w-4" /> {label}
    </Button>
  );

  return (
    <div data-testid="crm-module" className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <NavBtn id="board" icon={Table2} label="Tabel etape" />
        <NavBtn id="azi" icon={CalendarCheck} label="Azi" />
        <NavBtn id="pipeline" icon={LayoutGrid} label="Pipeline" />
        <NavBtn id="rapoarte" icon={BarChart3} label="Rapoarte" />
        {isBroker && <NavBtn id="setari" icon={Settings} label="Setări CRM" />}
        {!isAdmin && (
          <Button size="sm" className="ml-auto bg-blue-600 hover:bg-blue-700"
            onClick={() => { if (!settings) loadSettings(); setShowAdd(true); }} data-testid="crm-quick-add">
            <UserPlus className="h-4 w-4 mr-1" /> Adăugare rapidă
          </Button>
        )}
      </div>

      {view === 'board' && <BoardView api={api} onOpenClient={openClient} key={`board-${refreshKey}`} />}
      {view === 'azi' && <AziView api={api} onOpenClient={openClient} key={`azi-${refreshKey}`} />}
      {view === 'pipeline' && <PipelineView api={api} settings={settings} onOpenClient={openClient} key={`pipe-${refreshKey}`} />}
      {view === 'rapoarte' && <ReportsView api={api} key={`rep-${refreshKey}`} />}
      {view === 'setari' && isBroker && <SetariView api={api} settings={settings} isBroker={isBroker} onSaved={loadSettings} />}
      {view === 'client' && selectedClient && (
        <ClientFisa api={api} settings={settings} clientId={selectedClient}
          onBack={() => { setView('board'); setSelectedClient(null); setRefreshKey((k) => k + 1); }}
          onChanged={() => setRefreshKey((k) => k + 1)} />
      )}

      <QuickAddDialog api={api} settings={settings} open={showAdd} onClose={() => setShowAdd(false)}
        onCreated={(id) => { setShowAdd(false); openClient(id); setRefreshKey((k) => k + 1); }} />
    </div>
  );
}

function QuickAddDialog({ api, settings, open, onClose, onCreated }) {
  const [f, setF] = useState({ nume: '', prenume: '', telefon: '', email: '', sursa: '', tipIntrare: 'nou', etapaInitiala: 1 });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.nume || !f.telefon) { toast.error('Nume și telefon sunt obligatorii'); return; }
    setSaving(true);
    try {
      const r = await api.post('/clients', { ...f, etapaInitiala: Number(f.etapaInitiala) });
      toast.success('Client adăugat');
      setF({ nume: '', prenume: '', telefon: '', email: '', sursa: '', tipIntrare: 'nou', etapaInitiala: 1 });
      onCreated(r.client.id);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Există deja un client cu acest telefon/email în firmă');
      } else {
        toast.error(e?.response?.data?.detail || 'Eroare la adăugare');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="crm-add-dialog">
        <DialogHeader><DialogTitle>Adăugare rapidă client</DialogTitle>
          <DialogDescription>Consultantul și data intrării se completează automat.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Nume *</Label>
            <Input value={f.nume} onChange={(e) => set('nume', e.target.value)} data-testid="crm-add-nume" />
          </div>
          <div>
            <Label className="text-xs">Prenume</Label>
            <Input value={f.prenume} onChange={(e) => set('prenume', e.target.value)} data-testid="crm-add-prenume" />
          </div>
          <div>
            <Label className="text-xs">Telefon *</Label>
            <Input value={f.telefon} onChange={(e) => set('telefon', e.target.value)} data-testid="crm-add-telefon" />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input value={f.email} onChange={(e) => set('email', e.target.value)} data-testid="crm-add-email" />
          </div>
          <div>
            <Label className="text-xs">Sursă</Label>
            <select className="w-full border rounded-md h-10 px-2 text-sm" value={f.sursa}
              onChange={(e) => set('sursa', e.target.value)} data-testid="crm-add-sursa">
              <option value="">—</option>
              {(settings?.surse || []).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Etapă inițială (clienți vechi)</Label>
            <select className="w-full border rounded-md h-10 px-2 text-sm" value={f.etapaInitiala}
              onChange={(e) => set('etapaInitiala', e.target.value)} data-testid="crm-add-etapa">
              <option value={1}>1 – Lead</option>
              <option value={2}>2 – Client</option>
              <option value={3}>3 – Dosar</option>
              <option value={4}>4 – Retenție</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Anulează</Button>
          <Button onClick={submit} disabled={saving} data-testid="crm-add-submit">
            {saving ? 'Se adaugă…' : 'Adaugă client'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
